import { createFileRoute } from "@tanstack/react-router";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useEffect, useState } from "react";
import { Loader2, BrainCircuit, Zap, Clock, CheckCircle2, XCircle } from "lucide-react";
import { RenataPageChrome } from "@/components/renata/RenataPageChrome";
import { BusinessEmptyState } from "@/components/renata/BusinessEmptyState";

export const Route = createFileRoute("/app/renata/usage")({
  component: RenataUsagePage,
  head: () => ({ meta: [{ title: "Consumo IA — Renata" }] }),
});

interface UsageData {
  total_queries: number;
  total_tokens: number;
  actions_approved: number;
  actions_cancelled: number;
  avg_duration_ms: number;
}

interface CreditsData {
  balance: number;
  unlimited: boolean;
  creditsIncludedMonth: number | null;
  usedThisPeriod: number;
  remaining: number | null;
  status: "unlimited" | "healthy" | "warning" | "critical" | "exhausted";
  period: string;
  periodStart?: string;
  periodEnd?: string;
}

function Tile({
  icon: Icon,
  label,
  value,
  sub,
  accent,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
}) {
  return (
    <div className={`rounded-xl border bg-card p-4 flex gap-3 items-start ${accent ? "border-primary/30 bg-primary/5" : ""}`}>
      <div className="mt-0.5 rounded-lg bg-muted p-1.5">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="mt-0.5 text-xl font-semibold tabular-nums leading-tight">{value}</p>
        {sub ? <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p> : null}
      </div>
    </div>
  );
}

function fmtDate(iso: string) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("es-AR", {
    day: "numeric", month: "short", timeZone: "UTC",
  });
}

function CreditsBar({ credits }: { credits: CreditsData }) {
  const { unlimited, creditsIncludedMonth, usedThisPeriod, remaining, status, periodStart, periodEnd } = credits;

  const periodLabel =
    periodStart && periodEnd
      ? `${fmtDate(periodStart)} – ${fmtDate(periodEnd)}`
      : credits.period;

  if (unlimited) {
    return (
      <div className="rounded-xl border bg-card p-4">
        <p className="text-xs text-muted-foreground">Créditos RENATA</p>
        <p className="mt-1 text-xl font-semibold">Ilimitados</p>
        <p className="text-xs text-muted-foreground mt-0.5">Plan Enterprise · {periodLabel}</p>
      </div>
    );
  }
  const total = creditsIncludedMonth ?? 0;
  const pct = total > 0 ? Math.min(100, Math.round((usedThisPeriod / total) * 100)) : 0;
  const rem = remaining ?? 0;
  const barColor =
    status === "exhausted" || status === "critical" ? "bg-red-500"
    : status === "warning" ? "bg-amber-400"
    : "bg-emerald-500";

  return (
    <div className="rounded-xl border bg-card p-4 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">Créditos RENATA del mes</p>
        <span className="text-xs font-medium tabular-nums">
          {rem.toLocaleString("es-AR")} restantes
        </span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-muted-foreground">
        {usedThisPeriod.toLocaleString("es-AR")} de {total.toLocaleString("es-AR")} usados · {periodLabel}
      </p>
    </div>
  );
}

function RenataUsagePage() {
  const tenantId = useWorkspaceTenantId();
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [credits, setCredits] = useState<CreditsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) return;
    void Promise.all([
      fetch(`/api/renata/usage?days=30`, {
        credentials: "include",
        headers: { "X-Tenant-Id": tenantId },
      }).then((r) => r.json() as Promise<{ usage: UsageData }>).then((d) => setUsage(d.usage)),
      fetch(`/api/renata/credits`, {
        credentials: "include",
        headers: { "X-Tenant-Id": tenantId },
      }).then((r) => r.json() as Promise<CreditsData>).then((d) => setCredits(d)),
    ]).finally(() => setLoading(false));
  }, [tenantId]);

  function formatDuration(ms: number): string {
    return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(1)} s`;
  }

  const hasActivity = usage && Number(usage.total_queries) > 0;

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="mx-auto max-w-2xl space-y-4">
        <RenataPageChrome
          screenId="usage"
          title="Consumo IA"
          subtitle="Actividad de los últimos 30 días."
        />

        {loading ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : null}

        {!loading && credits ? <CreditsBar credits={credits} /> : null}

        {!loading && !hasActivity ? <BusinessEmptyState screenId="generic" /> : null}

        {!loading && hasActivity ? (
          <div className="grid grid-cols-2 gap-3">
            <Tile icon={BrainCircuit} label="Consultas" value={Number(usage!.total_queries).toLocaleString("es-AR")} sub="Mensajes con Renata" />
            <Tile icon={Clock} label="Tiempo promedio" value={formatDuration(Number(usage!.avg_duration_ms))} sub="Por respuesta" />
            <Tile icon={CheckCircle2} label="Cambios aprobados" value={String(Number(usage!.actions_approved))} />
            <Tile icon={XCircle} label="Cambios cancelados" value={String(Number(usage!.actions_cancelled))} />
            {credits && !credits.unlimited && (
              <div className="col-span-2">
                <Tile icon={Zap} label="Créditos consumidos" value={(credits.usedThisPeriod).toLocaleString("es-AR")} sub="Este mes · del plan incluido" />
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
